import {
  ChakraFlex,
  Text,
  Icon,
  FAIconName,
  IconSize,
  Tag,
  TagColor,
  FireflyButton,
} from '@equip.health/ui';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { SurveyName } from '~/lib/constants/survey/SurveyName';
import { GroupedAssessment } from '~/lib/types/survey/GroupedAssessment';
import { SurveyResponseStatus } from '~/lib/constants/survey/SurveyResponseStatus';
import {
  abbreviateAssessmentDuration,
  calculateDaysLeftIn30DayWindowFromAdmitDate,
} from '~/lib/util/assessments.util';
interface OverdueAssessmentCardProps {
  groupedAssessment: GroupedAssessment;
  admitDate: string;
  onClickCompleteTogether: (surveyResponse: SurveyResponse) => void;
  /** Borderless row for a container card, with the duration unit shortened. */
  isFlat?: boolean;
}

const OverdueAssessmentCard = ({
  groupedAssessment,
  admitDate,
  onClickCompleteTogether,
  isFlat = false,
}: OverdueAssessmentCardProps) => {
  const overdueAssessment = groupedAssessment.assessments[0];
  const OVERDUE_TAG_ID = `overdue-tag-${overdueAssessment.responseExternalId}`;
  const COMPLETE_TOGETHER_LINK_ID = `complete-together-link-${overdueAssessment.responseExternalId}`;

  const latestCompletedSurvey = groupedAssessment?.assessments.find(
    (survey) => survey.status === SurveyResponseStatus.DONE,
  );

  const now = DateTime.now().startOf('day');
  const daysLeft = calculateDaysLeftIn30DayWindowFromAdmitDate(
    overdueAssessment.createdDate,
    admitDate,
  );

  const daysSinceLastCompleted = latestCompletedSurvey
    ? now.diff(
        DateTime.fromISO(latestCompletedSurvey.endDate).startOf('day'),
        'days',
      ).days
    : null;

  const lastCompletedText = useMemo(() => {
    if (daysSinceLastCompleted === 0) {
      return 'Last completed today';
    } else if (daysSinceLastCompleted === 1) {
      return 'Last completed yesterday';
    } else if (daysSinceLastCompleted !== null) {
      return `Last completed ${daysSinceLastCompleted} days ago`;
    } else {
      return 'Not previously completed';
    }
  }, [daysSinceLastCompleted]);

  const overdueSurveyAbbrev = useMemo(() => {
    let displayName = overdueAssessment.surveyName;
    if (overdueAssessment.surveyName === SurveyName.EDE_Q) {
      displayName = 'EDE-Q';
    } else if (overdueAssessment.surveyName === SurveyName.QUALITY_OF_LIFE) {
      displayName = 'CIA';
    }
    return displayName;
  }, [overdueAssessment.surveyName]);

  const overdueSurveyTitle = `${groupedAssessment.displayName} (${overdueSurveyAbbrev})`;

  const durationText = isFlat
    ? abbreviateAssessmentDuration(groupedAssessment.estimatedDuration)
    : groupedAssessment.estimatedDuration;

  if (daysLeft !== undefined && daysLeft === 0) {
    return null;
  }

  return (
    <ChakraFlex
      border={isFlat ? 'none' : '1px solid'}
      borderColor="neutral.line"
      borderRadius={isFlat ? 0 : '12px'}
      flexDirection="column"
      gap={2}
      p={isFlat ? 0 : 4}
      width={isFlat ? '100%' : undefined}
    >
      <ChakraFlex alignItems="center" justifyContent="space-between">
        <Text data-testid="overdueSurveyTitle" variant="bodyMedium">
          {overdueSurveyTitle}
        </Text>
        <ChakraFlex
          alignItems="center"
          flexShrink={isFlat ? 0 : undefined}
          gap={2}
        >
          <Icon
            color="neutral.secondary"
            name={FAIconName.clockRegular}
            size={IconSize.small}
          />
          <Text
            color="neutral.secondary"
            data-testid="overdueSurveyDuration"
            variant="caption"
            whiteSpace={isFlat ? 'nowrap' : undefined}
          >
            {durationText}
          </Text>
        </ChakraFlex>
      </ChakraFlex>
      <Text color="neutral.secondary" variant="caption">
        {lastCompletedText}
      </Text>
      <ChakraFlex alignItems="center" justifyContent="space-between">
        <Tag
          color={TagColor.red}
          id={OVERDUE_TAG_ID}
          isBorderRequired={false}
          value={`${daysLeft} day${daysLeft === 1 ? '' : 's'} left`}
        />
        <FireflyButton
          id={COMPLETE_TOGETHER_LINK_ID}
          leftIcon={FAIconName.penRegular}
          onClick={() => onClickCompleteTogether(overdueAssessment)}
          size="small"
          variant="tertiary"
        >
          Complete together
        </FireflyButton>
      </ChakraFlex>
    </ChakraFlex>
  );
};

export default OverdueAssessmentCard;
